<template>
  <teleport to="#home" :disabled="!mobileScreen">
    <div
      v-if="selectedCategory"
      class="main-body"
      @mouseenter="showMenu()"
      @mouseleave="hideMenu()"
    >
      <div v-if="mobileScreen" class="mobile-top-bar">
        <ListMenuHeader
          :leftBox="currentView == 'LEFT'"
          @closeOverlay="closeOverlay"
          @resetAll="resetAll"
          @goBack="goBack"
        />
      </div>
      <div class="left-title-box">
        <div class="main-title">
          <div>
            {{ title }}
          </div>
          <div v-if="currentView == 'RIGHT'" class="selected-category-title">
            {{ selectedCategory?.name ?? '' }}
          </div>
        </div>
        <div v-if="currentView == 'LEFT' || currentView == 'ALL'" class="left-box">
          <div class="single-select-box">
            <ListCategory
              :categories="categories"
              :selectedCategoryId="selectedCategory?.id ?? ''"
              @onSelectCategory="onSelectCategory"
            />
            <div v-if="!mobileScreen" class="reset-button">
              <button class="reset-button-style" @click="resetAll()">
                {{ t('general__data__reset-all-choices') }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="currentView == 'RIGHT' || currentView == 'ALL'" class="right-box">
        <div class="select-all-box">
          <SelectAllCheckbox :selectAll="allCategoryTypesSelected" @selectAll="markAllTypes" />
        </div>
        <div class="types-select-box">
          <ListTypeItems :typeItems="selectedCategory?.types ?? []" @emitId="emitId" />
        </div>
      </div>
    </div>
  </teleport>
</template>
<script lang="ts" setup>
import { ref, computed, onBeforeUnmount, watch } from 'vue'
import { useStore } from 'vuex'
import ListMenuHeader from '@/components/general/lists/ListMenuWithCheckbox/ListMenuHeader.vue'
import ListCategory from '@/components/general/lists/ListMenuWithCheckbox/ListCategory.vue'
import ListTypeItems from '@/components/general/lists/ListMenuWithCheckbox/ListTypeItems.vue'
import SelectAllCheckbox from '@/components/general/lists/ListMenuWithCheckbox/SelectAllCheckbox.vue'
import { orderAlphabetical } from '@/utils/sort'
import { useGeneralStore } from '@/pinia'
import { useI18n } from 'vue-i18n'

export interface CategoryItem {
  id: string
  name: string
  types: TypeItem[]
  visible: boolean
}
export interface TypeItem {
  id: string
  color: string
  name: string
  disabled: boolean
  visible: boolean
}
interface EMITS {
  (e: 'onChecklistToggle', typeId: any, categoryId: any, checked: boolean)
  (e: 'closedOverlay')
  (e: 'onHoverListMenu')
  (e: 'closeHoverListMenu')
}
interface PROPS {
  delay?: string // Used for multiselect delay, value as string in ms
  title: string
  categories: CategoryItem[]
}

const emits = defineEmits<EMITS>()
const props = defineProps<PROPS>()
const { t } = useI18n()
const store = useStore()
const piniaStore = useGeneralStore()
const selectedCategory = ref<CategoryItem | undefined>(undefined)
const allCategoryTypesSelected = computed(
  () => selectedCategory.value?.types?.every((type) => type.visible) ?? false
)

const mobileScreen = computed<string>(() => store.state.mobileScreen)
const currentMobileView = ref<'LEFT' | 'RIGHT'>('LEFT')
const currentView = computed<'ALL' | 'LEFT' | 'RIGHT'>(() => {
  if (mobileScreen.value) {
    return currentMobileView.value
  } else {
    return 'ALL'
  }
})

onBeforeUnmount(() => {
  store.commit('SET_SHOW_ZONE_MENU', false)
  store.commit('SET_POI_MENU', false)
  piniaStore.setShowLineMenu(false)
})

function closeOverlay() {
  store.commit('SET_SHOW_ZONE_MENU', false)
  store.commit('SET_POI_MENU', false)
  piniaStore.setShowLineMenu(false)
}

function emitId(typeId, event) {
  emits('onChecklistToggle', typeId, selectedCategory.value?.id, event)
}

function goBack() {
  currentMobileView.value = 'LEFT'
}

function hideMenu() {
  emits('closeHoverListMenu')
}

async function markAllTypes(allSelected: boolean) {
  if (!selectedCategory.value) return
  for (let type of selectedCategory.value.types) {
    if (props.delay) {
      const delayInteger = parseInt(props.delay)
      await new Promise((resolve) => setTimeout(resolve, delayInteger)) // Currently (for example) line sources generated in Pinia store have to be loaded with a delay to not be overwritten
    }
    emits('onChecklistToggle', type.id, selectedCategory.value.id, allSelected)
  }
}

function onSelectCategory(categoryId: string) {
  selectedCategory.value = props.categories.find((category) => category.id === categoryId)!
  currentMobileView.value = 'RIGHT'
  selectedCategory.value.types.sort(orderAlphabetical)
}

function resetAll() {
  for (let category of props.categories) {
    for (let type of category.types) {
      if (type.visible === true) {
        emits('onChecklistToggle', type.id, category.id, false)
      }
    }
  }
}

function showMenu() {
  if (!mobileScreen.value) emits('onHoverListMenu')
}

watch(
  () => props.categories,
  (newVal, oldVal) => {
    if (!oldVal?.length && newVal.length) selectedCategory.value = props.categories[0]
  },
  { immediate: true }
)
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/mixins.scss';

.main-body {
  background: #f8f8f8;
  color: #000000;
  border-radius: 4px;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.25);
  width: 432px;
  height: 307px;
  bottom: 84px; // Should redo positioning depending on distance from button
  position: absolute;
  display: flex;
  flex-direction: row;
  font-family: 'Raleway', sans-serif;
  margin-right: 20px;
}
.main-title {
  width: 0;
  padding: 15px 15px 10px 22px;
  font-size: 14px;
  font-weight: 800;
  color: #6a6a6a;
}
.left-title-box {
  display: flex;
  flex-direction: column;
  width: 50%;
  border-right: 1px solid #e9e8e7;
}
.single-select-box {
  padding-left: 11px;
  padding-right: 11px;
}
.types-select-box {
  padding-right: 15px;
  padding-top: 15px;
}
.right-box {
  width: 50%;
}
.select-all-box {
  padding-top: 30px;
  border-bottom: 1px solid #e9e8e7;
  margin-left: 10px;
  margin-right: 15px;
}
.select-all-input {
  padding: 5px 5px 5px 0px;
}
.reset-button {
  padding-bottom: 20px;
  position: absolute;
  bottom: 0;
}
.reset-button-style {
  background-color: #2f363b;
  border: hidden;
  border-radius: 4px;
  color: white;
  font-weight: 400;
  font-size: 14px;
  font-family: Raleway, serif;
  padding: 4px 8px 4px 8px;
  cursor: pointer;
  letter-spacing: 0.4px;
}
.selected-category-title {
  padding-top: 16px;
  font-size: 16px;
}
@media screen and (max-width: 1024px) {
  .main-body {
    align-items: center;
    background-color: #2f363b;
    border-radius: 0;
    bottom: unset;
    color: white;
    flex-direction: column;
    height: calc(100vh - variables.$layout-top-bar-height);
    left: 0;
    margin: 0;
    max-width: 100%;
    padding: 24px;
    position: absolute;
    top: variables.$layout-top-bar-height;
    width: 100%;
    z-index: 120;
  }
  .select-all-box {
    padding: 0;
    margin: 0;
  }
  .selected-category-title {
    font-weight: 600;
  }
  .single-select-box {
    display: flex;
    justify-content: center;
    padding: 0;
  }
  .types-select-box {
    padding-top: 0;
    overflow-y: auto;
    @include mixins.scrollbar-vertical;
  }
  .left-title-box {
    border-right: 0;
    overflow: hidden;
    width: 100%;
  }
  .left-box {
    max-height: 55vh;
    width: 100%;
    color: white;
    border-right: 0;
    overflow: auto;
    @include mixins.scrollbar-vertical;

    &.leftDisabled {
      display: none;
    }
  }
  .main-title {
    color: white;
    padding: 26px 0;
    text-align: center;
    justify-content: center;
    display: flex;
    width: 100%;
    font-size: 32px;
    flex-direction: column;
  }
  .reset-button {
    padding-bottom: 24px;
    padding-left: 20px;
    position: relative;
    bottom: 0;
  }
  .right-box {
    max-height: 50vh;
    width: 100%;
    row-gap: 24px;
    max-width: 642px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    &.rightDisabled {
      display: none;
    }
  }
  .mobile-top-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }
}
</style>
